import { debounce } from "lodash"
import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"
import { payoffCellKey, readPayoffAxesFromNodeData, type PayoffAxesConfig } from "../payoff_matrix_logic"
import { downloadPayoffMatrixCsv, parsePayoffMatrixCsv } from "../payoff_matrix_csv"

interface PayoffMatrixProps {
  getAxes: () => PayoffAxesConfig
  getValue: (key: string) => string
  setValue: (key: string, value: string) => void
  importPayoffValues: (values: Record<string, string>) => void
  getExportFilename: () => string
}

const PayoffMatrixView = ({ getAxes, getValue, setValue, importPayoffValues, getExportFilename }: PayoffMatrixProps) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const axes = getAxes()
  const cellCount = axes.a.length * axes.b.length
  const expanded = cellCount > 4

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result || "")
      const updates = parsePayoffMatrixCsv(text, axes)
      if (Object.keys(updates).length > 0) {
        importPayoffValues(updates)
        forceUpdate()
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
    reader.readAsText(file)
  }

  const cell = (aValue: number, bValue: number, aLabel: string, bLabel: string) => {
    const key = payoffCellKey(aValue, bValue)
    const code = `${aValue}/${bValue}`
    const title = `A: ${aLabel}, B: ${bLabel}`
    return (
      <td className="payoff-matrix-cell" key={key}>
        <input
          type="number"
          className="form-control form-control-sm payoff-matrix-cell__input"
          title={title}
          aria-label={title}
          placeholder={code}
          value={getValue(key)}
          step="any"
          onChange={e => {
            setValue(key, e.target.value)
            forceUpdate()
          }}
          onPointerDown={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
        />
      </td>
    )
  }

  return (
    <div
      className={`payoff-matrix-control${expanded ? " payoff-matrix-control--expanded" : ""}`}
      data-payoff-cols={axes.b.length}
      data-payoff-rows={axes.a.length}
    >
      <div className="payoff-matrix-toolbar">
        <button
          type="button"
          className="btn btn-sm btn-secondary payoff-matrix-toolbar__btn"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            if (fileInputRef.current) {
              fileInputRef.current.click()
            }
          }}
        >
          <i className="fa fa-upload mr-1" aria-hidden="true" />
          Import CSV
        </button>
        <button
          type="button"
          className="btn btn-sm btn-secondary payoff-matrix-toolbar__btn"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            downloadPayoffMatrixCsv(axes, getValue, getExportFilename())
          }}
        >
          <i className="fa fa-download mr-1" aria-hidden="true" />
          Export CSV
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="payoff-matrix-toolbar__file"
          onChange={handleImportCsv}
          onPointerDown={e => e.stopPropagation()}
        />
      </div>
      <table className="payoff-matrix-table">
        <thead>
          <tr>
            <th className="payoff-matrix-corner" aria-hidden />
            {axes.b.map(b => (
              <th
                key={`b-head-${b.value}`}
                className={`payoff-matrix-colhead${expanded ? " payoff-matrix-colhead--vertical" : ""}`}
                title={`B = ${b.label}`}
              >
                <span className="payoff-matrix-head-text">{expanded ? b.label : `B = ${b.label}`}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {axes.a.map(a => (
            <tr key={`a-row-${a.value}`}>
              <th className="payoff-matrix-rowhead" title={a.label}>
                <span className="payoff-matrix-head-text">A = {a.label}</span>
              </th>
              {axes.b.map(b => cell(a.value, b.value, a.label, b.label))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export class PayoffMatrixControl extends Control {
  props: PayoffMatrixProps
  component: (props: PayoffMatrixProps) => JSX.Element
  type = "PayoffMatrixControl"

  constructor(emitter: Emitter<EventsTypes> | null, controlKey = "payoffMatrix") {
    super(controlKey)
    const process = debounce(() => emitter?.trigger("process"), 400)

    this.props = {
      getAxes: () => readPayoffAxesFromNodeData(this.getNode().data as Record<string, unknown>),
      getValue: (key: string) => {
        const v = this.getData(key)
        if (typeof v === "number" && Number.isFinite(v)) return String(v)
        if (typeof v === "string") return v
        return ""
      },
      setValue: (key: string, value: string) => {
        if (value === "" || value === "-" || value === "." || value === "-.") {
          delete (this.getNode().data as Record<string, unknown>)[key]
        } else {
          const parsed = Number(value)
          this.putData(key, Number.isNaN(parsed) ? value : parsed)
        }
        process()
      },
      importPayoffValues: (values: Record<string, string>) => {
        const data = this.getNode().data as Record<string, unknown>
        Object.keys(values).forEach(key => {
          const raw = values[key]
          if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
            delete data[key]
          } else {
            const parsed = Number(raw)
            data[key] = Number.isNaN(parsed) ? raw : parsed
          }
        })
        process()
      },
      getExportFilename: () => {
        const name = this.getNode().data.name
        if (typeof name === "string" && name.trim() !== "") {
          return `${name.trim()}-payoff-matrix`
        }
        return "payoff-matrix"
      },
    }
    this.component = PayoffMatrixView
  }
}
